import { getAPIManagerInstance } from '../apiManager';
import { DECKARD_PAYMENTS_API_URL } from '../common';
import { paymentGet, paymentPost } from './paymentErrors';
import {
  ACHDirectDebitPaymentRequest,
  ACHDirectDebitPaymentResponse,
  CardConnectApiData,
  CheckPaymentRequest,
  ChequePaymentResponse,
  CheckPaymentStatusResponse,
  CreditCardPaymentRequest,
  CreditCardPaymentResponse,
  CyberSourceApiData,
  GenericPaymentRequest,
  GenericPaymentsResponse,
  PAYMENT_SOURCE,
  PayNothingRequest,
  PayNothingResponse,
  PostbackResponse,
  StripePublicKeyResponse,
} from 'src/types/payment';

export const getStripePublicKey = async (): Promise<StripePublicKeyResponse> =>
  paymentGet<StripePublicKeyResponse>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/stripePublic`,
    { params: { source: PAYMENT_SOURCE } },
    'Failed to retrieve Stripe public key'
  );

export const payGeneric = async (
  request: Omit<GenericPaymentRequest, 'source'>,
  authToken: string
): Promise<GenericPaymentsResponse> => {
  const body: GenericPaymentRequest = {
    ...request,
    source: PAYMENT_SOURCE,
  };
  return paymentPost<GenericPaymentsResponse, GenericPaymentRequest>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payGeneric`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to initiate generic payment'
  );
};

export const payByCreditCard = async (
  request: Omit<CreditCardPaymentRequest, 'source'>,
  authToken: string
): Promise<CreditCardPaymentResponse> => {
  const body: CreditCardPaymentRequest = {
    ...request,
    source: PAYMENT_SOURCE,
  };
  return paymentPost<CreditCardPaymentResponse, CreditCardPaymentRequest>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCC`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to initiate credit card payment'
  );
};

export const payByACHDirectDebit = async (
  request: Omit<ACHDirectDebitPaymentRequest, 'source'>,
  authToken: string
): Promise<ACHDirectDebitPaymentResponse> => {
  const body: ACHDirectDebitPaymentRequest = {
    ...request,
    source: PAYMENT_SOURCE,
  };
  return paymentPost<
    ACHDirectDebitPaymentResponse,
    ACHDirectDebitPaymentRequest
  >(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payACHDirectDebit`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to initiate ACH direct debit payment'
  );
};

export const payByCheck = async (
  request: Omit<CheckPaymentRequest, 'source'>,
  authToken: string
): Promise<ChequePaymentResponse> => {
  const body: CheckPaymentRequest = {
    ...request,
    source: PAYMENT_SOURCE,
  };
  return paymentPost<ChequePaymentResponse, CheckPaymentRequest>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCheque`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to initiate cheque payment'
  );
};

export const payNothing = async (
  request: Omit<PayNothingRequest, 'source' | 'subtotal'>,
  authToken: string
): Promise<PayNothingResponse> => {
  const body: PayNothingRequest = {
    ...request,
    source: PAYMENT_SOURCE,
    subtotal: 0,
  };
  return paymentPost<PayNothingResponse, PayNothingRequest>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payNothing`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to initiate payment for no amount'
  );
};

const paymentStatus = async (
  authToken: string,
  paymentID: string
): Promise<CheckPaymentStatusResponse> =>
  paymentGet<CheckPaymentStatusResponse>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/paymentStatus/${paymentID}`,
    { headers: { Authorization: `Bearer ${authToken}` } },
    'Failed to check payment status'
  );

export const checkTaxPayment = paymentStatus;
export const checkLicensePayment = paymentStatus;

export const cybersourcePostback = async (
  data: CyberSourceApiData
): Promise<PostbackResponse> =>
  paymentPost<PostbackResponse, CyberSourceApiData>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cybersourcePostback`,
    data,
    {},
    'Failed to process Cybersource postback'
  );

export const cardConnectPostback = async (
  data: CardConnectApiData
): Promise<PostbackResponse> =>
  paymentPost<PostbackResponse, CardConnectApiData>(
    `${DECKARD_PAYMENTS_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cardConnectPostback`,
    data,
    {},
    'Failed to process CardConnect postback'
  );
